import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { objectToFormData } from 'src/app/core/util/FormData-converter';
import { InvoiceActivity } from 'src/app/modules/bookkeeping/models/activity/activity';
import { CreditNoteDTO } from 'src/app/modules/bookkeeping/models/credit-note/credit-note';
import { ArchiveInvoice, ArchiveRequest } from 'src/app/modules/bookkeeping/models/invoice/archive-request';
import { BillsToPayInvoiceDetailsRequest } from 'src/app/modules/bookkeeping/models/invoice/bills-to-pay-invoice-details-request';
import { CreatedInvoice } from 'src/app/modules/bookkeeping/models/invoice/created-invoice';
import { CreatedInvoiceWithLines } from 'src/app/modules/bookkeeping/models/invoice/created-invoice-with-lines';
import { InboxInvoices } from 'src/app/modules/bookkeeping/models/invoice/inbox-invoices';
import { Invoice } from 'src/app/modules/bookkeeping/models/invoice/invoice';
import { AccountCodingSuggestionApiResponse, CodingSuggestionApiResponse, RecordCodingFeedbackRequest } from 'src/app/modules/bookkeeping/models/invoice/account-coding-suggestion';
import { SyncedExpClaims } from 'src/app/modules/bookkeeping/models/invoice/invoice-exp-claims';
import { InvoiceShort, InvoiceUnReconciledAdd } from 'src/app/modules/bookkeeping/models/invoice/invoice-short';
import { KeyValuePair } from 'src/app/modules/bookkeeping/models/invoice/key-value-pairs';
import { RecognizedInvoiceService } from 'src/app/modules/bookkeeping/services/recognized-invoice.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { BankTransactionDTO } from '../../modules/bookkeeping/models/expense/bank-transactions';
import { HttpInternalService } from './http.service';
import { HttpClient } from '@angular/common/http';
import { AsyncSubject, EMPTY, from, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  /** Uploads in flight at once when several documents are submitted together. */
  private static readonly UPLOAD_CONCURRENCY = 3;

  constructor(
    private _httpService: HttpInternalService,
    private _recognizedInvoiceService: RecognizedInvoiceService,
    private _toasterMessageService: ToasterMessageService,
    private http: HttpClient
  ) { }

  uploadInvoice(invoice: CreatedInvoice) {
    const formData = objectToFormData(invoice);
    this._toasterMessageService.displayToasterState(ToasterTypes.Info, 'Uploading', 'Submitting document for processing');

    this._httpService.postClearRequest('Invoices/uploadInvoice', formData)
      .pipe(take(1))
      .subscribe(
        (invoice: Invoice) => this.onInvoiceUploaded(invoice),
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  /**
   * Several documents, each its own upload: one request per file, so each is recognised,
   * deduplicated and lands in the inbox on its own, and one that fails does not stop the rest.
   * At most UPLOAD_CONCURRENCY are in flight at once, so a large batch does not swamp
   * document processing for everyone else in the organisation.
   */
  uploadInvoices(files: File[], organisationId: string) {
    if (!files?.length) {
      return;
    }
    if (files.length === 1) {
      this.uploadInvoice({ organisationId, invoice: files[0], recordId: '', isBankTransMode: false });
      return;
    }
    this._toasterMessageService.displayToasterState(
      ToasterTypes.Info, 'Uploading', `Submitting ${files.length} documents for processing`);

    from(files)
      .pipe(mergeMap(file => {
        const formData = objectToFormData({ organisationId, invoice: file, recordId: '', isBankTransMode: false } as CreatedInvoice);
        return this._httpService.postClearRequest('Invoices/uploadInvoice', formData).pipe(
          take(1),
          tap((invoice: Invoice) => this.onInvoiceUploaded(invoice)),
          catchError((e: Error) => {
            this._toasterMessageService.displayToasterState(
              ToasterTypes.Error, 'Error', `${file.name}: ${e?.message || 'upload failed'}`);
            return EMPTY;
          })
        );
      }, InvoiceService.UPLOAD_CONCURRENCY))
      .subscribe();
  }

  private onInvoiceUploaded(invoice: Invoice) {
    if (invoice != null) {
      this._recognizedInvoiceService.send(invoice);
      this.writePairsToLocalStorage(invoice);
    }
  }

  // Return observable for callers that want to react to success/failure
  uploadInvoiceObservable(invoice: CreatedInvoice) {
    const formData = objectToFormData(invoice);
    this._toasterMessageService.displayToasterState(ToasterTypes.Info, 'Uploading', 'Submitting document for processing');
    return this._httpService.postClearRequest<any>('Invoices/uploadInvoice', formData);
  }

  private writePairsToLocalStorage(invoice: Invoice) {
    // An upload response without an id has nothing to key the pairs on.
    if (!invoice?.id) {
      return;
    }
    const invoiceId = invoice.id.toString();
    const storage = localStorage.getItem('StoredPairs');

    if (storage) {
      let storedPairs = [...JSON.parse(storage)] as KeyValuePair[];
      let existedPair = storedPairs.find(p => p.key === invoiceId);
      if (!existedPair && invoice.pairs) {
        let pairs = {
          key: invoiceId,
          value: invoice.pairs
        } as KeyValuePair;
        storedPairs.push(pairs);
      } else if (existedPair) {
        existedPair.key = invoiceId;
        existedPair.value = invoice.pairs;
      }
      localStorage.setItem('StoredPairs', JSON.stringify(storedPairs));
    } else {
      let pairs = [{
        key: invoiceId,
        value: invoice.pairs
      } as KeyValuePair];
      localStorage.setItem('StoredPairs', JSON.stringify(pairs));
    }
  }

  publishInvoice(invoice: CreatedInvoiceWithLines) {
    return this._httpService.postClearRequest('Invoices/publishInvoice', invoice);
  }

  publishCreditNote(creditNote: CreditNoteDTO) {
    return this._httpService.postClearRequest('Invoices/publishCreditNote', creditNote);
  }

  publishReceipt(bankTransaction: BankTransactionDTO) {
    return this._httpService.postClearRequest('Invoices/PublishReceipt', bankTransaction);
  }

  publishReceiptQBO(bankTransaction: CreatedInvoiceWithLines) {
    return this._httpService.postClearRequest('Invoices/PublishReceiptQBO', bankTransaction);
  }
  updateInvoice(invoice: CreatedInvoiceWithLines) {
    return this._httpService.putRequest<boolean>('Invoices/updateInvoice', invoice);
  }

  updateInvoiceAfterPublish(invoice: CreatedInvoiceWithLines) {
    return this._httpService.putRequest<boolean>('Invoices/updateInvoiceAfterPublish', invoice);
  }

  GetReceiptAttachments(organizationId, invoiceID) {
    return this._httpService.getRequest(`Xero/GetReceiptAttachments?organizationId=${organizationId}&invoiceID=${invoiceID}`);
  }

  deleteInvoice(id: string) {
    return this._httpService.deleteRequest(`Invoices/deleteInvoice?id=${id}`);
  }

  getDownloadInvoiceUrl(id: string, organisationId: string, fileName: string = "") {
    return this._httpService.buildUrl(`Invoices/downloadInvoice?id=${id}&organisationId=${organisationId}&fileName=${fileName}`);
  }

  downloadFile(id: string, organisationId: string) {
    return this._httpService.getRequest<Blob>(`Invoices/downloadInvoice?id=${id}&organisationId=${organisationId}`, '', 'blob');
  }

  downloadZipArchive(archiveRequest: ArchiveRequest) {
    return this._httpService.postRequest<Blob>(`Invoices/DownloadZipArchive`, archiveRequest, null, 'blob');
  }

  archiveInvoice(archivedInvoice: ArchiveInvoice) {
    return this._httpService.postRequest(`Invoices/ArchiveInvoice`, archivedInvoice);
  }

  unarchiveInvoice(archivedInvoice: ArchiveInvoice) {
    return this._httpService.postRequest(`Invoices/UnarchiveInvoice`, archivedInvoice);
  }

  getInvoicesUploadedByUser(
    id: string,
    page: number = 1,
    pageSize: number = 1000,
    sort: string = 'desc',
    searchText: string = ''
  ) {
    // Removed isProcessed from API call
    return this._httpService.getRequest<any>(`Invoices/getInvoicesUploadedByUser?id=${id}&page=${page}&pageSize=${pageSize}&sort=${sort}&searchText=${searchText}`);
  }

  getInvoicesRecentActivity(
    id: string) {
    return this._httpService.getRequest<any>(`Invoices/GetInvoicesRecentActivity?id=${id}`);
  }

  /** `take` is rows per group (details needed / ready to publish); the counts are of everything. */
  getInvoicesDashboardSummary(id: string, take: number = 5) {
    return this._httpService.getRequest<any>(`Invoices/getInvoicesDashboardSummary?id=${id}&take=${take}`);
  }

  getInboxInvoicesUploadedByUser(id: string, page: number, pageSize: number, sortDirection: string, isProcessed: boolean | null = null, searchText: string = '') {
    const processedParam = (isProcessed === null || isProcessed === undefined) ? '' : `&isProcessed=${isProcessed}`;
    return this._httpService.getRequest<InboxInvoices>(`Invoices/getInvoicesUploadedByUser?id=${id}&page=${page}&pageSize=${pageSize}&sort=${sortDirection}${processedParam}&searchText=${searchText}`);
  }

  getInvoiceById(id: string) {
    return this._httpService.getRequest<Invoice>(`Invoices/getInvoiceById?id=${id}`);
  }

  getBankNameByBSB(bsb: string) {
    return this._httpService.getRequest<{ name: string }>(`Invoices/getBankNameByBSB?bsb=${bsb}`);
  }

  getAccountCodingSuggestions(refId: string, organizationId: string) {
    return this._httpService.getRequest<CodingSuggestionApiResponse>(
      `Invoices/getCodingSuggestions?refID=${refId}&organizationid=${organizationId}`
    );
  }

  recordCodingFeedback(payload: RecordCodingFeedbackRequest) {
    return this._httpService.postClearRequest<{ status: string }>(
      'Invoices/recordCodingFeedback',
      payload
    );
  }

  getInvoiceByReferenceId(id: string, orgId: string, referenceID?: string) {
    return this._httpService.getRequest<Invoice>(`Invoices/getInvoiceByReferenceId?id=${id}&organisationId=${orgId}&referenceID=${referenceID}`);
  }

  getInvoiceActivityById(invoiceId: string, organizationId: string) {
    return this._httpService.getRequest<InvoiceActivity[]>(`Invoices/getInvoiceActivityById?invoiceId=${invoiceId}&organizationId=${organizationId}`);
  }

  getInvoiceDetailsForBillsToPay(requestData: BillsToPayInvoiceDetailsRequest) {
    return this._httpService.postRequest<Invoice>('Invoices/GetInvoiceDetailsForBillsToPay', requestData);
  }

  getAllExpClaimsByOrganizationId(organizationId: string, userId: string) {
    return this._httpService.getRequest<any>(`${"Reporting/getExpClaimsByOrganizationId" + "?organisationId=" + organizationId + "&userid=" + userId}`);
  }

  createExpenseReport(syncedExpClaims: SyncedExpClaims) {
    return this._httpService.postClearRequest<{ id: string }>('Reporting/createExpReport', syncedExpClaims);
  }

  createDefaultInvoice(orgnazationId: string) {
    return this._httpService.getRequest('Invoices/GenerateDefaultInvoice?orgnazationId=' + orgnazationId);
  }
  updateSuggestedMatches(model: any) {
    return this._httpService.postRequest('Invoices/UpdateSuggestedMatches', model);
  }

  unMatchTransaction(model: any) {
    return this._httpService.postRequest('Invoices/UnmatchTransaction', model);
  }

  updateInactiveSuggestedMatches(model: any) {
    return this._httpService.postRequest('Invoices/UpdateInactiveSuggestedMatches', model);
  }

  getRelatedData(organizationId: string, relatedId: string) {
    return this._httpService.getRequest<any>(`Invoices/getRelatedData?organizationId=${organizationId}&relatedId=${relatedId}`);
  }

  /**
   * Documents not yet matched to any transaction, for matching one by hand. The server
   * limits a submitter to their own. `amount` only orders the list: closest first.
   */
  getUnmatchedExpenses(organizationId: string, search: string, amount: number) {
    const params = [`organizationId=${organizationId}`];
    if (search && search.trim()) { params.push(`search=${encodeURIComponent(search.trim())}`); }
    if (amount || amount === 0) { params.push(`amount=${amount}`); }
    return this._httpService.getRequest<any>(`Invoices/getUnmatchedExpenses?${params.join('&')}`);
  }

  getMatchData(organizationId: string, relatedId: string) {
    return this._httpService.getRequest<any>(`Invoices/getMatchData?organizationId=${organizationId}&relatedId=${relatedId}`);
  }

  /**
   * AI-suggested coding (account + contact) for one or many transactions.
   * Returns one entry per requested RefID, in request order — a RefID with no
   * suggestions still comes back with empty suggestedAccountCode/Contact arrays.
   * Each array is sorted by confidence desc; index [0] is the recommended pick.
   * Unhandled server errors return 204 No Content (empty body).
   */
  getCodingSuggestions(organizationId: string, refIds: string[]) {
    return this._httpService.postRequest<any[]>('Invoices/getCodingSuggestions', { organizationId, refIds });
  }

  /**
   * Bulk-code N selected transactions in a single round-trip. The BE creates
   * an invoice per row using shared coding fields (supplier / category /
   * details / tax / etc.) and per-row data derived server-side from the
   * unreconciled transaction (amount / date / bank account / statement line).
   *
   * Response: { status, requestedCount, succeededCount, failedCount,
   *   results: [{ transactionId, status: 'success'|'failed', invoiceId?, reason? }] }
   *
   * See backend-bulk-code-transactions.md for the full contract.
   */
  bulkCodeTransactions(payload: any) {
    return this._httpService.postRequest<any>('Invoices/bulkCodeTransactions', payload);
  }

  /**
   * Also returns the outcome, so a caller can tell a failure from "still uploading". Success
   * still reaches everyone through RecognizedInvoiceService; a failure used to be a toast and
   * nothing else, which left the reconcile panel waiting on a result that never came. Callers
   * that ignore the return value behave exactly as before.
   */
  uploadInvoiceManually(invoice: InvoiceUnReconciledAdd): Observable<Invoice> {
    this._toasterMessageService.displayToasterState(ToasterTypes.Info, 'Uploading', 'Submitting document for processing');

    const outcome = new AsyncSubject<Invoice>();

    this._httpService.postClearRequest('Invoices/uploadInvoiceByManualUpload', invoice)
      .pipe(take(1))
      .subscribe(
        (invoice: Invoice) => {
          if (invoice != null) {
            this._recognizedInvoiceService.send(invoice);
            this.writePairsToLocalStorage(invoice);
          }
          outcome.next(invoice);
          outcome.complete();
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
          outcome.error(e);
        }
      );

    return outcome.asObservable();
  }

  assignInvoice(invoiceAssignRequestModal) {
    return this._httpService.postRequest<any>('Invoices/AssignInvoiceToUser', JSON.stringify(invoiceAssignRequestModal));
  }

  getExchangeRate(organisationCurrency: string, invoiceDate: string, total: number): Observable<any> {
    return this._httpService.getRequest<any>(`Invoices/GetExchangeRate?organisationCurrency=${organisationCurrency}&invoiceDate=${invoiceDate}&total=${total}`, null, null, false);
  }

  refreshXeroData(organizationId: string) {
    return this._httpService.getRequest<any>(`BillsToPay/RefreshTransactions?organizationId=${organizationId}`);
  }
  getTagsByInvoiceId(invoiceId: string, organizationId: string) {
    return this._httpService.getRequest<string[]>(`Invoices/GetTagsByInvoiceId?invoiceId=${invoiceId}&organizationId=${organizationId}`);
  }
  reSubmitInvoice(blobId: string, organizationId: string) {
    return this._httpService.postRequest<any>('Invoices/ReSubmitInvoice?blobId=' + blobId + '&OrganisationId=' + organizationId, null);
  }
}
